import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import { getByClassIdAndStudentIdAndMonthAPI } from "../../../services/portal/student/studentService";
import {
  formatDateTime,
  getMonthNameFromDate,
  scheduleDateTime,
  tableHeaderColor,
} from "../../../utility/Common";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import PaymentBanner from "../../../components/portal/PaymentBanner";
import { useAuth } from "../../../contexts/AuthContext";
import { getMyBankSlipsAPI } from "../../../services/portal/student/bankTransferService";
import PaymentPendingVerification from "../../../components/portal/PaymentPendingVerification";
import { RoutePaths } from "../../../utility/RoutePaths";

const ClassMonthDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const classId = searchParams.get("classId");
  const date = searchParams.get("date");
  const month = getMonthNameFromDate(date);
  const [loading, setLoading] = useState(false);
  const [paymentRecordLoading, setPaymentRecordLoading] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [classSchedules, setClassSchedules] = useState([]);
  const [paidStatus, setPaidStatus] = useState(0);
  const [pendingBankSlips, setPendingBankSlips] = useState([]);

  const loadClassDetails = useCallback(async () => {
    if (!classId) return;

    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        const classItem = res.Data.find((c) => c.ClassId === Number(classId));
        if (classItem) {
          setClassDetails(classItem);
        } else {
          showErrorToastMsg("Class not found");
        }
      }
    } catch (error) {
      console.error("Error loading class details:", error);
      showErrorToastMsg("Failed to load class details");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const loadClassSchedules = useCallback(async () => {
    if (!classId) return;

    setLoading(true);
    try {
      const res = await getByClassIdAndStudentIdAndMonthAPI(
        Number(classId),
        date,
      );
      if (res && res.IsSuccessful && res.Data) {
        setClassSchedules(res.Data || []);
      } else {
        setClassSchedules([]);
      }
    } catch (error) {
      console.error("Error loading class schedules:", error);
      setClassSchedules([]);
      setLoading(false);
    }
  }, [classId]);

  const getMyBankSlips = useCallback(async () => {
    if (!classId) return;

    setPaymentRecordLoading(true);
    try {
      var res = await getMyBankSlipsAPI(Number(classId), 2, date);
      if (res && res.IsSuccessful && res.Data) {
        setPaidStatus(1);
      } else {
        setPaidStatus(-1);

        var list = [];
        res = await getMyBankSlipsAPI(Number(classId), 1, date);
        if (res && res.IsSuccessful && res.Data) {
          list = res.Data;
        } else {
          list = [];

          // res = await getMyBankSlipsAPI(Number(classId), 9, date);
          // if (res && res.IsSuccessful && res.Data) {
          //   list = res.Data;
          // } else {
          //   list = [];
          // }
        }

        if (list.length === 0) {
          res = await getMyBankSlipsAPI(Number(classId), 4, date);
          if (res && res.IsSuccessful && res.Data) {
            list = res.Data;
          } else {
            list = [];
          }
        }

        setPendingBankSlips(list);
      }
      setPaymentRecordLoading(false);
    } catch (error) {
      console.error("Error loading getMyBankSlips:", error);
      setPendingBankSlips([]);
      setPaymentRecordLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (classId && user && user.UserId) {
      loadClassDetails();
      loadClassSchedules();
      getMyBankSlips();
    }
  }, [classId, user, loadClassDetails, loadClassSchedules, getMyBankSlips]);

  const openScheduleDetails = (scheduleId) => {
    navigate(
      `${RoutePaths.STUDENT_CLASS_SCHEDULE_DETAILS_PAGE}?classId=${classId}&scheduleId=${scheduleId}&month=${date}`,
    );
  };

  if (!classId || !date) {
    return (
      <DashboardLayout
        title="Class Month Details"
        subtitle="Class ID and Month are required"
      >
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            Unable to load class month details.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const drawImage = (data) => {
    const imgSrc = data.Image
      ? data.Image.startsWith("data:")
        ? data.Image
        : `data:image/jpeg;base64,${data.Image}`
      : "/images/no_image.jpg";
    return (
      <img
        src={imgSrc}
        alt={data.Description}
        className="w-60 h-60 object-cover transition-transform duration-300 group-hover:scale-105"
        style={{ width: 150, height: 150, objectFit: "contain" }}
      />
    );
  };

  return (
    <DashboardLayout>
      <div>
        <div className="mb-4">
          <button
            onClick={() =>
              navigate(
                `${RoutePaths.STUDENT_CLASS_DETAILS_PAGE}?classId=${classId}`,
              )
            }
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            ← Back to Class Details
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : (
          classDetails && (
            <>
              <ClassDetailsBox
                classDetails={classDetails}
                additionalTitle={month}
              />

              {paymentRecordLoading ? (
                <Loader />
              ) : pendingBankSlips.length > 0 ? (
                <PaymentPendingVerification
                  payment={pendingBankSlips}
                  classId={classDetails.ClassId}
                  date={date}
                />
              ) : (
                paidStatus === -1 && (
                  <PaymentBanner
                    price={classDetails.ClassFee}
                    classId={classDetails.ClassId}
                    date={date}
                  />
                )
              )}

              <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
                <div
                  className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
                >
                  <h4 className="text-md font-medium text-white">
                    Schedule Dates for {month}
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Start Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Join
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                          >
                            <Loader />
                          </td>
                        </tr>
                      ) : classSchedules.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                          >
                            No class schedules found.
                          </td>
                        </tr>
                      ) : (
                        classSchedules.map((scheduleItem, index) => (
                          <tr
                            key={scheduleItem.ClassScheduleId}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {drawImage(scheduleItem)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {paidStatus === 1 &&
                              (scheduleItem.RecordLink ||
                                scheduleItem.ZoomLink) ? (
                                <span
                                  onClick={() =>
                                    openScheduleDetails(
                                      scheduleItem.ClassScheduleId,
                                    )
                                  }
                                  className="text-primary-600 cursor-pointer text-blue px-3 py-2 rounded-md text-base font-medium transition duration-300"
                                >
                                  {scheduleItem.Description}
                                </span>
                              ) : (
                                scheduleItem.Description
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {scheduleDateTime(
                                scheduleItem.StartDate,
                                scheduleItem.EndDate,
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {paidStatus === 1 &&
                              (scheduleItem.RecordLink ||
                                scheduleItem.ZoomLink) ? (
                                <span
                                  onClick={() =>
                                    openScheduleDetails(
                                      scheduleItem.ClassScheduleId,
                                    )
                                  }
                                  className="bg-primary-600 cursor-pointer text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 transition duration-300"
                                >
                                  Join / Study Packs
                                </span>
                              ) : scheduleItem.IsFree === true ? (
                                <span
                                  onClick={() =>
                                    openScheduleDetails(
                                      scheduleItem.ClassScheduleId,
                                    )
                                  }
                                  className="bg-primary-600 cursor-pointer text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 transition duration-300"
                                >
                                  Free Class - Join
                                </span>
                              ) : paidStatus === -1 ? (
                                <span className="text text-red-600">
                                  Complete the payment to access.
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClassMonthDetailsPage;
